// WHAT: Component for managing SwitchBot secret key
// WHY:  Separates secret management UI from main component to reduce complexity
// DOMAIN: Settings → SwitchBot
// DEPENDENCIES: React, @/components/ui, lucide-react

import { Edit, Shield, Trash2 } from "lucide-react";
import React from "react";

import {
  Badge,
  Button,
  Card,
  Heading,
  Input,
  Label,
  Stack,
  StatusIndicator,
} from "@/components/ui";

interface SecretSectionProperties {
  /** Whether secret is being edited */
  editingSecret: boolean;
  /** Whether form is in loading state */
  loading: boolean;
  /** Handler for cancel edit */
  onCancelEdit: () => void;
  /** Handler for edit action */
  onEdit: () => void;
  /** Handler for remove action */
  onRemove: () => void;
  /** Handler for save action */
  onSave: () => void;
  /** Handler for secret change */
  onSecretChange: (value: string) => void;
  /** Saved secret value */
  savedSecret?: null | string;
  /** Secret input value */
  secret: string;
}

/**
 * WHAT:
 *   Component for managing SwitchBot secret key.
 *
 * WHY:
 *   Separates secret management UI from main component to reduce complexity.
 *
 * @param root0
 *
 * @param root0.editingSecret
 *
 * @param root0.loading
 *
 * @param root0.savedSecret
 *
 * @param root0.secret
 *
 * @param root0.onEdit
 *
 * @param root0.onRemove
 *
 * @param root0.onSave
 *
 * @param root0.onSecretChange
 *
 * @param root0.onCancelEdit
 *
 * @returns {JSX.Element} Secret section component
 *
 */
export const SecretSection: React.FC<SecretSectionProperties> = ({
  editingSecret,
  loading,
  onCancelEdit,
  onEdit,
  onRemove,
  onSave,
  onSecretChange,
  savedSecret,
  secret,
}) => {
  return (
    <Card className="bg-slate-800/50 border-slate-700/50" padding="lg" variant="default">
      <Stack direction="col" gap={4}>
        <Stack align="center" direction="row" gap={3}>
          <Shield className="w-5 h-5 text-green-400" />
          <Heading className="text-white" level="h3" size="md">
            Secret Key
          </Heading>
          <Badge size="sm" variant={savedSecret ? "success" : "warning"}>
            {savedSecret ? "Configured" : "Not Set"}
          </Badge>
        </Stack>

        {savedSecret && !editingSecret ? (
          <Stack direction="col" gap={3}>
            <StatusIndicator
              configuredText="Configured"
              description="Your SwitchBot secret key is configured for HMAC-SHA256 signing"
              isConfigured
              label="SwitchBot Secret Key"
              size="md"
            />
            <Stack direction="row" gap={2}>
              <Button
                className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                onClick={onEdit}
                size="sm"
                variant="outline"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
              <Button
                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                onClick={() => {
                  onRemove();
                }}
                size="sm"
                variant="outline"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Remove
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Stack direction="col" gap={3}>
            <StatusIndicator
              description="Enter your SwitchBot secret key for HMAC-SHA256 signing"
              isConfigured={false}
              label="SwitchBot Secret Key"
              notConfiguredText="Not Set"
              size="md"
            />
            <Stack direction="col" gap={2}>
              <Label className="text-slate-300" htmlFor="secret-input">
                Secret Key
              </Label>
              <Input
                className="bg-slate-700/50 border-slate-600/50"
                id="secret-input"
                onChange={(e) => {
                  onSecretChange(e.target.value);
                }}
                placeholder="Enter your SwitchBot secret key..."
                type="password"
                value={secret}
              />
              <Stack direction="row" gap={2}>
                <Button
                  className="bg-green-600/80 hover:bg-green-600 border border-green-500/50 hover:border-green-500 text-white"
                  disabled={loading || !secret}
                  onClick={() => {
                    onSave();
                  }}
                >
                  {loading ? "Saving..." : "Save Secret"}
                </Button>
                {editingSecret ? (
                  <Button onClick={onCancelEdit} size="sm" variant="outline">
                    Cancel
                  </Button>
                ) : null}
              </Stack>
            </Stack>
          </Stack>
        )}
      </Stack>
    </Card>
  );
};

